import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Radar,
  Download,
  Loader2,
  LogOut,
  RefreshCw,
  Users,
  Inbox,
  Inbox as InboxIcon,
  ClipboardCheck,
} from "lucide-react";
import {
  adminLogout,
  fetchAdminAuthStatus,
  fetchAdminWaitlist,
  ADMIN_WAITLIST_CSV_URL,
} from "@/lib/api";
import { ADMIN_WAITLIST } from "@/constants/testIds/admin";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function AttributionCell({ entry }) {
  // Show the most useful attribution tag as a compact chip. Prefer campaign
  // (usually names the specific tweet or thread), then source (twitter/etc),
  // then the `ref` shortcut. If nothing was captured, render a muted dash.
  const campaign = (entry.utm_campaign || "").trim();
  const source = (entry.utm_source || "").trim();
  const medium = (entry.utm_medium || "").trim();
  const refTag = (entry.ref || "").trim();

  const chips = [];
  if (campaign) chips.push({ label: "campaign", value: campaign });
  else if (refTag) chips.push({ label: "ref", value: refTag });
  if (source) chips.push({ label: "src", value: source });
  else if (medium) chips.push({ label: "med", value: medium });

  if (chips.length === 0) {
    return <span className="font-mono text-[10px] text-[#475569]">—</span>;
  }
  return (
    <div className="flex flex-wrap items-center gap-1">
      {chips.map((c) => (
        <span
          key={`${c.label}-${c.value}`}
          className="inline-flex items-center gap-1 border border-[#3b82f6]/40 bg-[#3b82f6]/10 px-1.5 py-0.5 font-mono text-[10px] text-[#3b82f6]"
        >
          <span className="text-[#64748b]">{c.label}</span>
          <span className="text-[#3b82f6]">{c.value}</span>
        </span>
      ))}
    </div>
  );
}

function WaitlistRow({ entry }) {
  return (
    <tr
      data-testid={ADMIN_WAITLIST.row(entry.email)}
      className="border-b border-[#1e293b] align-top transition-colors hover:bg-[#0f172a]/60"
    >
      <td className="whitespace-nowrap px-3 py-2.5 align-top font-mono text-[11px] text-[#94a3b8]">
        {fmtDate(entry.created_at)}
      </td>
      <td className="px-3 py-2.5 align-top text-sm text-[#f8fafc]">
        {entry.email}
      </td>
      <td className="px-3 py-2.5 align-top">
        {entry.source_note ? (
          <span className="border border-[#64748b]/40 bg-[#64748b]/10 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-[#94a3b8]">
            {entry.source_note}
          </span>
        ) : (
          <span className="font-mono text-[10px] text-[#475569]">—</span>
        )}
      </td>
      <td className="px-3 py-2.5 align-top">
        <AttributionCell entry={entry} />
      </td>
      <td className="whitespace-nowrap px-3 py-2.5 align-top font-mono text-[11px] text-[#64748b]">
        {entry.ip || "—"}
      </td>
    </tr>
  );
}

export default function AdminWaitlist() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking | authed | anon
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAdminWaitlist();
      setEntries(data?.entries || []);
      setTotal(data?.count || 0);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate("/admin/login", { replace: true });
        return;
      }
      toast.error("Failed to load waitlist");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    let alive = true;
    fetchAdminAuthStatus()
      .then((r) => {
        if (!alive) return;
        if (r?.authenticated) {
          setStatus("authed");
          load();
        } else {
          setStatus("anon");
          navigate("/admin/login", { replace: true });
        }
      })
      .catch(() => {
        if (alive) {
          setStatus("anon");
          navigate("/admin/login", { replace: true });
        }
      });
    return () => {
      alive = false;
    };
  }, [load, navigate]);

  const onLogout = async () => {
    try {
      await adminLogout();
    } finally {
      navigate("/admin/login", { replace: true });
    }
  };

  if (status !== "authed") {
    return (
      <div
        data-testid="admin-waitlist-checking"
        className="fixed inset-0 z-50 bg-[#0b1120]"
        aria-hidden="true"
      />
    );
  }

  return (
    <div
      data-testid={ADMIN_WAITLIST.page}
      className="min-h-screen bg-[#0b1120] text-[#f8fafc]"
    >
      <header className="sticky top-0 z-30 border-b border-[#1e293b] bg-[#0b1120]/95 backdrop-blur">
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-2 px-4 py-3 md:flex-nowrap md:gap-6 md:px-6">
          <Link to="/" className="flex shrink-0 items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center border border-[#f59e0b]/40 bg-[#f59e0b]/10">
              <Radar className="h-4 w-4 text-[#f59e0b]" />
            </div>
            <div className="leading-tight">
              <div className="text-sm font-bold tracking-tight text-[#f8fafc]">
                SIGNAL EDGE
              </div>
              <div className="hidden font-mono text-[9px] uppercase tracking-[0.2em] text-[#f59e0b] sm:block">
                admin · waitlist
              </div>
            </div>
          </Link>

          <nav className="ml-2 flex items-center gap-1">
            <Link
              to="/admin/review"
              data-testid={ADMIN_WAITLIST.reviewNavLink}
              className="inline-flex items-center gap-1.5 border border-[#1e293b] bg-[#111827] px-2.5 py-1 font-mono text-[11px] uppercase tracking-wider text-[#94a3b8] transition-colors hover:border-[#334155] hover:text-[#f8fafc]"
            >
              <ClipboardCheck className="h-3.5 w-3.5" /> Review
            </Link>
            <span className="inline-flex items-center gap-1.5 border border-[#f59e0b] bg-[#f59e0b]/15 px-2.5 py-1 font-mono text-[11px] uppercase tracking-wider text-[#f59e0b]">
              <Users className="h-3.5 w-3.5" /> Waitlist
            </span>
          </nav>

          <div className="flex-1" />

          <span
            data-testid={ADMIN_WAITLIST.countBadge}
            className="border border-[#1e293b] bg-[#111827] px-2.5 py-1 font-mono text-[11px] uppercase tracking-wider text-[#94a3b8]"
          >
            {total} signup{total === 1 ? "" : "s"}
          </span>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            data-testid={ADMIN_WAITLIST.refreshButton}
            className="inline-flex items-center gap-1.5 border border-[#1e293b] bg-[#111827] px-2.5 py-1 font-mono text-[11px] uppercase tracking-wider text-[#94a3b8] transition-colors hover:border-[#334155] hover:text-[#f8fafc] disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          {/* Direct <a> so the browser handles the file download; using the
              same-origin admin session cookie for auth. */}
          <a
            href={ADMIN_WAITLIST_CSV_URL}
            data-testid={ADMIN_WAITLIST.exportButton}
            className="inline-flex items-center gap-1.5 border border-[#3b82f6] bg-[#3b82f6] px-2.5 py-1 font-mono text-[11px] uppercase tracking-wider text-white transition-colors hover:bg-[#2563eb]"
          >
            <Download className="h-3.5 w-3.5" /> Export CSV
          </a>
          <button
            type="button"
            onClick={onLogout}
            data-testid={ADMIN_WAITLIST.logoutButton}
            className="inline-flex items-center gap-1.5 border border-[#1e293b] bg-[#111827] px-2.5 py-1 font-mono text-[11px] uppercase tracking-wider text-[#94a3b8] transition-colors hover:border-[#334155] hover:text-[#f8fafc]"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-4 py-6 md:px-6">
        <div className="mb-4 flex flex-col gap-1">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569]">
            M4 · Waitlist
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-[#f8fafc]">
            Landing page signups
          </h1>
          <p className="text-sm text-[#94a3b8]">
            Newest first. Emails are deduped case-insensitively at write time —
            each row here is a distinct human. Attribution captured from
            <code className="mx-1 border border-[#1e293b] bg-[#0f172a] px-1 font-mono text-[11px] text-[#94a3b8]">?utm_source=…</code>
            and <code className="mx-1 border border-[#1e293b] bg-[#0f172a] px-1 font-mono text-[11px] text-[#94a3b8]">?ref=…</code>
            on the landing URL (persisted 30 days per visitor). Export CSV for launch-day email.
          </p>
        </div>

        {loading ? (
          <div
            data-testid={ADMIN_WAITLIST.loading}
            className="flex items-center justify-center border border-[#1e293b] bg-[#111827] py-16 text-sm text-[#94a3b8]"
          >
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading waitlist…
          </div>
        ) : entries.length === 0 ? (
          <div
            data-testid={ADMIN_WAITLIST.emptyState}
            className="flex flex-col items-center justify-center border border-dashed border-[#1e293b] bg-[#0f172a] py-20 text-center"
          >
            <InboxIcon className="mb-3 h-8 w-8 text-[#334155]" />
            <div className="text-sm font-semibold text-[#f8fafc]">No signups yet</div>
            <div className="mt-1 max-w-sm text-xs text-[#94a3b8]">
              When someone joins the waitlist from the landing page, they'll
              appear here. Point Twitter traffic at the landing to start filling this list.
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto border border-[#1e293b] bg-[#111827]">
            <table
              data-testid={ADMIN_WAITLIST.table}
              className="w-full min-w-[880px] text-left text-sm"
            >
              <thead className="border-b border-[#1e293b] bg-[#0f172a] font-mono text-[10px] uppercase tracking-wider text-[#64748b]">
                <tr>
                  <th className="px-3 py-2 font-normal">Joined</th>
                  <th className="px-3 py-2 font-normal">Email</th>
                  <th className="px-3 py-2 font-normal">Source</th>
                  <th className="px-3 py-2 font-normal">Attribution</th>
                  <th className="px-3 py-2 font-normal">IP</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <WaitlistRow key={e.email} entry={e} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-6 border-t border-[#1e293b] pt-4 text-[11px] leading-relaxed text-[#64748b]">
          Signal Edge · Waitlist rows are never surfaced publicly. IP and user-agent
          are captured for spam triage only — strip them before any external export.
        </div>
      </main>
    </div>
  );
}
